/**
 * Airport Intelligence Service
 *
 * Provides navigation guidance and lounge information for supported airports.
 * Designed with a 3-second timeout and graceful fallback to null when data
 * is unavailable or the service fails.
 *
 * NOTE: The Airport_Intelligence backend is not yet deployed. This service
 * returns stub data for known airports and null for unknown ones. When the
 * backend is available, replace the stub with real API calls.
 */

/** @typedef {import('../types/tripcard').AirportIntelligenceData} AirportIntelligenceData */

/**
 * @typedef {Object} LoungeInfo
 * @property {string} name - Lounge name
 * @property {string} terminal - Terminal location
 * @property {number} walkingTimeMinutes - Walking time from current position
 * @property {string} directions - Walking directions to the lounge
 * @property {string[]} memberships - Accepted membership programs
 */

/**
 * @typedef {Object} NavigationOptions
 * @property {number} [timeout=3000] - Request timeout in milliseconds
 */

/**
 * @typedef {Object} LoungeMemberships
 * @property {boolean} [admiralsClub] - American Airlines Admirals Club
 * @property {boolean} [skyClub] - Delta Sky Club
 * @property {boolean} [centurionLounge] - Amex Centurion Lounge
 * @property {boolean} [priorityPass] - Priority Pass
 */

const DEFAULT_TIMEOUT = 3000;

/**
 * Stub navigation data for known airports.
 * Replace with real API integration when Airport_Intelligence backend is deployed.
 * @type {Record<string, Record<string, AirportIntelligenceData>>}
 */
const STUB_NAVIGATION_DATA = {
  DFW: {
    D15: {
      gateRelativeDirections: 'Turn RIGHT out of Gate D15 → follow signs to Terminal Exit',
      walkingTimeMinutes: 6,
      baggageCarousel: 'Carousel 7',
      terminalExitDirections: 'Follow signs toward Skylink or proceed to Terminal D exit',
    },
    A22: {
      gateRelativeDirections: 'Turn LEFT out of Gate A22 → follow signs to Skylink',
      walkingTimeMinutes: 10,
      baggageCarousel: 'Carousel 3',
      terminalExitDirections: 'Take Skylink to Terminal A baggage claim',
    },
  },
  JFK: {
    B12: {
      gateRelativeDirections: 'Turn LEFT out of Gate B12 → follow signs to Terminal Exit',
      walkingTimeMinutes: 8,
      baggageCarousel: 'Carousel 4',
      terminalExitDirections: 'Proceed straight to AirTrain / Ground Transportation',
    },
  },
  LAX: {
    '52A': {
      gateRelativeDirections: 'Turn RIGHT out of Gate 52A → follow signs to baggage claim',
      walkingTimeMinutes: 12,
      baggageCarousel: 'Carousel 2',
      terminalExitDirections: 'Follow LAX-it signs for rideshare pickup',
    },
  },
  ORD: {
    C18: {
      gateRelativeDirections: 'Turn LEFT out of Gate C18 → follow signs to Terminal 3 exit',
      walkingTimeMinutes: 7,
      baggageCarousel: 'Carousel 5',
      terminalExitDirections: 'Follow signs to CTA Blue Line or Ground Transportation',
    },
  },
};

/**
 * Stub lounge data for known airports.
 * @type {Record<string, Record<string, LoungeInfo[]>>}
 */
const STUB_LOUNGE_DATA = {
  DFW: {
    D: [
      {
        name: 'Admirals Club',
        terminal: 'D',
        walkingTimeMinutes: 4,
        directions: 'Near Gate D21, upper level',
        memberships: ['admiralsClub'],
      },
      {
        name: 'Centurion Lounge',
        terminal: 'D',
        walkingTimeMinutes: 6,
        directions: 'Between Gates D12 and D14, upper level',
        memberships: ['centurionLounge'],
      },
    ],
    A: [
      {
        name: 'Admirals Club',
        terminal: 'A',
        walkingTimeMinutes: 3,
        directions: 'Near Gate A25',
        memberships: ['admiralsClub'],
      },
    ],
  },
  JFK: {
    B: [
      {
        name: 'Delta Sky Club',
        terminal: 'B',
        walkingTimeMinutes: 5,
        directions: 'Near Gate B37, upper level',
        memberships: ['skyClub'],
      },
    ],
  },
  ORD: {
    C: [
      {
        name: 'United Club',
        terminal: 'C',
        walkingTimeMinutes: 3,
        directions: 'Near Gate C16, mezzanine level',
        memberships: ['priorityPass'],
      },
    ],
  },
};

/**
 * Creates a timeout promise that rejects after the specified duration.
 * @param {number} ms - Timeout in milliseconds
 * @returns {Promise<never>}
 */
function createTimeout(ms) {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
  });
}

/**
 * Simulates fetching navigation guidance data.
 * In production, this would be an API call to the Airport_Intelligence backend.
 * @param {string} airportCode
 * @param {string} gate
 * @returns {Promise<AirportIntelligenceData|null>}
 */
async function fetchNavigationData(airportCode, gate) {
  // Simulate async behavior
  await new Promise((resolve) => setTimeout(resolve, 50));

  const airportData = STUB_NAVIGATION_DATA[airportCode];
  if (!airportData) return null;

  const gateData = airportData[gate];
  if (!gateData) return null;

  return { ...gateData };
}

/**
 * Simulates fetching lounge information.
 * In production, this would be an API call to the Airport_Intelligence backend.
 * @param {string} airportCode
 * @param {string} terminal
 * @param {LoungeMemberships} memberships
 * @returns {Promise<LoungeInfo[]|null>}
 */
async function fetchLoungeData(airportCode, terminal, memberships) {
  // Simulate async behavior
  await new Promise((resolve) => setTimeout(resolve, 50));

  const airportLounges = STUB_LOUNGE_DATA[airportCode];
  if (!airportLounges) return null;

  const terminalLounges = airportLounges[terminal];
  if (!terminalLounges) return null;

  // Filter lounges by user's memberships
  const membershipKeys = Object.entries(memberships)
    .filter(([, hasAccess]) => hasAccess)
    .map(([key]) => key);

  if (membershipKeys.length === 0) return null;

  const accessibleLounges = terminalLounges.filter((lounge) =>
    lounge.memberships.some((m) => membershipKeys.includes(m))
  );

  return accessibleLounges.length > 0 ? accessibleLounges : null;
}

/**
 * Retrieves navigation guidance for a specific airport and gate.
 * Returns partial data when some fields are available but others aren't.
 * Falls back to null on timeout or network error.
 *
 * @param {string} airportCode - IATA airport code (e.g., "DFW", "JFK")
 * @param {string} gate - Gate identifier (e.g., "D15", "B12")
 * @param {NavigationOptions} [options] - Configuration options
 * @returns {Promise<AirportIntelligenceData|null>} Navigation data or null on failure
 */
export async function getNavigationGuidance(airportCode, gate, options = {}) {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT;

  try {
    const result = await Promise.race([
      fetchNavigationData(airportCode, gate),
      createTimeout(timeout),
    ]);

    return result;
  } catch (error) {
    console.error(
      `[AirportIntelligenceService] Failed to get navigation guidance for ${airportCode} gate ${gate}:`,
      error.message
    );
    return null;
  }
}

/**
 * Retrieves lounge information for a specific airport terminal,
 * filtered by the traveler's lounge memberships.
 * Falls back to null on timeout or network error.
 *
 * @param {string} airportCode - IATA airport code (e.g., "DFW", "JFK")
 * @param {string} terminal - Terminal identifier (e.g., "D", "B")
 * @param {LoungeMemberships} memberships - Traveler's lounge access memberships
 * @returns {Promise<LoungeInfo[]|null>} Lounge info array or null on failure
 */
export async function getLoungeInfo(airportCode, terminal, memberships) {
  try {
    const result = await Promise.race([
      fetchLoungeData(airportCode, terminal, memberships),
      createTimeout(DEFAULT_TIMEOUT),
    ]);

    return result;
  } catch (error) {
    console.error(
      `[AirportIntelligenceService] Failed to get lounge info for ${airportCode} terminal ${terminal}:`,
      error.message
    );
    return null;
  }
}

export default {
  getNavigationGuidance,
  getLoungeInfo,
};
